<script setup lang="ts">
import { type PropType } from 'vue';
import { type DropdownOption } from 'naive-ui';
import GaiaAvatar from '../../base/GaiaAvatar.vue';
import GaiaDropdown from '../../base/GaiaDropdown.vue';
import GaiaIconButton from '../../base/GaiaIconButton.vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaIconButtonOverrides, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** Display name / email shown next to the avatar. */
  name: {
    type: String,
    required: true,
  },
  /** Avatar initials, e.g. "JM". */
  initials: {
    type: String,
    default: '',
  },
  /** Faint sub-line under the name, e.g. "GAIA · org admin". */
  description: {
    type: String,
    default: undefined,
  },
  /**
   * Entries of the menu the trailing control opens, naive's own option shape —
   * so `children` gives submenus and `type: 'divider'` gives rules. The control
   * owns the menu rather than the page: it is the row's only affordance, so an
   * empty list would leave a button that does nothing (it opens disabled).
   */
  menuOptions: {
    type: Array as PropType<DropdownOption[]>,
    default: () => [],
  },
});

// The identity block is inert on purpose: every action on the account lives in
// the trailing control's menu, so there is exactly one click target here.
const emit = defineEmits<{ 'menu-select': [key: string | number] }>();

const hoverWash = gaiaNeutral.surfaceHover;
// The trailing control keeps its .trailing class: GaiaSidebar hides it in the
// collapsed rail via :deep(.gaia-user-row .trailing).
const iconOverrides = {
  ...gaiaIconButtonOverrides,
  colorHover: hoverWash,
  colorPressed: hoverWash,
};
</script>

<template>
  <div class="gaia-user-row">
    <div class="main">
      <slot name="avatar">
        <gaia-avatar>{{ props.initials }}</gaia-avatar>
      </slot>
      <span class="texts">
        <gaia-text truncate tone="text">{{ props.name }}</gaia-text>
        <gaia-text v-if="props.description" truncate size="micro" tone="faint">
          {{ props.description }}
        </gaia-text>
      </span>
    </div>
    <!-- The button is the menu's anchor, so the menu's bottom-left corner lands
         on the button's top-left one. Wrapping anything larger (the whole row)
         would drag the menu back to the row's left edge.

         Left on the default click trigger deliberately: naive fires its
         `clickoutside` on mouseup, ahead of the trigger's own click, so driving
         `show` by hand would close the menu and let the click reopen it — the
         cog could never close what it opened. -->
    <gaia-dropdown
      v-if="$slots.trailing"
      :options="props.menuOptions"
      :disabled="!props.menuOptions.length"
      placement="top-start"
      @select="key => emit('menu-select', key)"
    >
      <gaia-icon-button
        class="trailing"
        :dimension="26"
        :theme-overrides="iconOverrides"
        :style="{ flex: 'none', borderRadius: '8px' }"
      >
        <slot name="trailing" />
      </gaia-icon-button>
    </gaia-dropdown>
  </div>
</template>

<style scoped>
/* Sidebar account row from mock 5a — a bespoke row (naive has no equivalent);
   typography and washes from the shared tokens. The avatar and name are a flat
   label, not a control: the trailing button's menu is the only affordance. */
.gaia-user-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 2px;
}

.texts {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
</style>
